"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

function ParticlesBackground() {
  const [particles, setParticles] = useState<
    Array<{
      width: number;
      height: number;
      left: number;
      top: number;
      background: string;
      duration: number;
      delay: number;
    }>
  >([]);

  useEffect(() => {
    const colors = ["#10b981", "#06b6d4", "#3b82f6", "#22c55e", "#14b8a6"];
    setParticles(
      Array.from({ length: 30 }, () => ({
        width: Math.random() * 4 + 1,
        height: Math.random() * 4 + 1,
        left: Math.random() * 100,
        top: Math.random() * 100,
        background: colors[Math.floor(Math.random() * 5)],
        duration: 3 + Math.random() * 4,
        delay: Math.random() * 5,
      })),
    );
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-20"
          style={{
            width: `${p.width}px`,
            height: `${p.height}px`,
            left: `${p.left}%`,
            top: `${p.top}%`,
            background: p.background,
            animation: `float ${p.duration}s ease-in-out infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

const features = [
  {
    href: "/prepa",
    title: "Sideboard",
    desc: "Choisis le matchup, prépare Go 1st / Go 2nd, et vois ce qu'il te manque pour jouer le plan.",
    gradient: "from-emerald-600/20 to-cyan-600/20",
    border: "hover:border-emerald-500/60",
    shadow: "hover:shadow-emerald-500/20",
    iconBg: "from-emerald-500 to-cyan-500",
    tag: "Cœur de l'app",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    href: "/inventaire",
    title: "Inventaire",
    desc: "Importe ta collection (CSV ou CardNexus) et compare-la à tes decks pour cibler les achats.",
    gradient: "from-cyan-600/20 to-blue-600/20",
    border: "hover:border-cyan-500/60",
    shadow: "hover:shadow-cyan-500/20",
    iconBg: "from-cyan-500 to-blue-500",
    tag: "Import CSV / API",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    href: "/decks",
    title: "Decks",
    desc: "Construis ou importe tes listes, sideboard inclus, puis enchaîne sur la prépa matchup.",
    gradient: "from-blue-600/20 to-indigo-600/20",
    border: "hover:border-blue-500/60",
    shadow: "hover:shadow-blue-500/20",
    iconBg: "from-blue-500 to-indigo-500",
    tag: "Listes + SB",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    ),
  },
];

export default function HomePage() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-[#060611] text-white relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-emerald-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-600/10 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-600/5 rounded-full blur-[100px]" />
        <ParticlesBackground />
      </div>

      <div
        className={`relative z-10 container mx-auto px-6 py-12 transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 pt-8">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl blur-xl opacity-30 animate-glow-pulse" />
                <div className="relative w-20 h-20 glass rounded-2xl flex items-center justify-center">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/rifthub-logo.png"
                    alt="RiftHub"
                    className="h-16 w-16 object-contain drop-shadow-lg"
                  />
                </div>
              </div>
            </div>

            <h1 className="text-6xl md:text-8xl font-black mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-text-gradient bg-[length:200%_200%]">
                RiftHub
              </span>
            </h1>
            <p className="text-xl md:text-2xl font-medium text-gray-300 mb-3 tracking-wide">
              Sideboard-first pour Riftbound
            </p>
            <p className="text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
              Prépare tes plans de side par matchup, croise avec ton inventaire, et ouvre
              Cardmarket / CardNexus pour les cartes qui manquent.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
              <Link
                href="/prepa"
                className="group relative px-8 py-4 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/25"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-cyan-600" />
                <span className="relative">Ouvrir le sideboard</span>
              </Link>
              <Link
                href="/inventaire"
                className="px-8 py-4 rounded-2xl font-bold text-lg glass hover:bg-white/10 transition-all duration-300 hover:scale-105 border border-white/10 hover:border-white/20"
              >
                Importer l&apos;inventaire
              </Link>
            </div>
          </div>

          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-2">
                <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  Du matchup à l&apos;achat
                </span>
              </h2>
              <p className="text-gray-500">Side · inventaire · decks</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {features.map((f, i) => (
                <Link key={f.href} href={f.href}>
                  <div
                    className={`group relative p-7 rounded-2xl glass card-hover border border-white/5 ${f.border} transition-all duration-300 hover:shadow-2xl ${f.shadow} h-full`}
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <div
                      className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${f.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    />
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-5">
                        <div
                          className={`w-14 h-14 rounded-xl bg-gradient-to-br ${f.iconBg} flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300`}
                        >
                          {f.icon}
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-gray-300 border border-white/10">
                          {f.tag}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-2">{f.title}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                        {f.desc}
                      </p>
                      <div className="mt-5 flex items-center gap-1 text-gray-500 group-hover:text-gray-300 transition-colors">
                        <span className="text-xs font-medium">Commencer</span>
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
